import React, { useState } from "react";
import {
	Grid,
	ToggleButtonGroup,
	ToggleButton,
	Typography,
	Card,
	Tooltip,
} from "@mui/material";
import { UserWithTopics } from "../../context/ConfigContext/types";
import { ScraperConfig } from "../TopicsPanel/types";
import { ConfigPanelListProps } from "../TopicsPanel/ConfigPanelList";
import { UserFormProps } from "../UsersPanel/UserForm";
import CircleTwoToneIcon from "@mui/icons-material/CircleTwoTone";
import { UserWithClients } from "../../context/ClientAccessContext/types";
import { MainUserFormProps } from "../MainUsersPanel/MainUserForm";
import { useAdminPanelContext } from "../../context/AdminPanelContext";

export enum PanelType {
	SCRAPER_CONFIG = "Config",
	SCRAPER = "Scraper",
	CLIENT_USER = "Client User",
	MAIN_USER = "Main User",
}

export type PanelProps =
	| ConfigPanelListProps
	| UserFormProps
	| MainUserFormProps;

interface HasId {
	id: number;
}

type GenericPanelToggleProps = {
	items: UserWithTopics[] | ScraperConfig[] | UserWithClients[];
	panelType: PanelType;
	handleAddNew: (newUnsavedId: number) => void;
	RenderForm: (props: PanelProps) => JSX.Element;
};

const GenericPanelToggle: React.FC<GenericPanelToggleProps> = ({
	items,
	panelType,
	RenderForm,
	handleAddNew,
}) => {
	const { setSelectedConfigId } = useAdminPanelContext();
	const [selectedItemId, setSelectedItemId] = useState<number>(null);
	const [lastUnsavedId, setLastUnsavedId] = useState(0);

	const handleAddNewItem = () => {
		const newUnsavedId = lastUnsavedId - 1;
		handleAddNew(newUnsavedId);
		setSelectedItemId(newUnsavedId);
		setLastUnsavedId(newUnsavedId);
	};

	const handleItemSelect = (
		event: React.MouseEvent<HTMLElement>,
		itemId: number
	) => {
		if (panelType === PanelType.SCRAPER_CONFIG && itemId) {
			setSelectedConfigId(itemId);
		}

		if (itemId === selectedItemId) {
			event.stopPropagation();
			return;
		}
		if (itemId === -999) return handleAddNewItem();

		setSelectedItemId(itemId);
	};

	const getItem = <T extends HasId>(items: T[]): T | undefined => {
		return items.find((item) => item.id === selectedItemId);
	};

	return (
		<Grid container spacing={22} sx={{ paddingTop: 3 }}>
			<Grid item xs={3}>
				<Card
					style={{
						width: "fit-content",
						maxHeight: "calc(100vh - 130px)",
						minHeight: "fit-content",
						overflowY: "auto",
					}}
				>
					<ToggleButtonGroup
						color="primary"
						size="large"
						orientation="vertical"
						value={selectedItemId ? selectedItemId : ""}
						exclusive
						onChange={handleItemSelect}
					>
						<ToggleButton key={-998} disabled value="Users:">
							{panelType}s
						</ToggleButton>
						<ToggleButton key={-999} sx={{ color: "green" }} value={-999}>
							+ Add {panelType}
						</ToggleButton>
						{items?.length &&
							items.map((item) => (
								<ToggleButton
									key={item.id}
									value={item.id}
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
									}}
								>
									{item.id > 0 ? (
										<>
											{panelType === PanelType.SCRAPER_CONFIG && (
												<>
													<Tooltip
														arrow
														title={
															<Typography variant="caption">
																Scraper config
																<br />
																Id: {item.id}
																<br />
																ScraperId: {item.scraper.id}
																<br />
																Scraper name: {item.scraper.name}
																<br />
																Scraper type: {item.scraper.type}
															</Typography>
														}
													>
														{item.name && item.name !== "0" ? (
															<Typography>{item.name}</Typography>
														) : (
															<Typography>{`${item.scraper.type} - ${item.scraper.name} `}</Typography>
														)}
													</Tooltip>
													<CircleTwoToneIcon
														style={{ color: item.active ? "green" : "red" }}
													/>
												</>
											)}
											{panelType === PanelType.CLIENT_USER && (
												<Typography>{`${(item as UserWithTopics).firstName} ${
													(item as UserWithTopics).lastName
												} `}</Typography>
											)}
											{panelType === PanelType.MAIN_USER && (
												<Typography>{`${(item as UserWithClients).firstName} ${
													(item as UserWithClients).lastName
												} `}</Typography>
											)}
										</>
									) : (
										`Unsaved ${panelType} ${-item.id}`
									)}
								</ToggleButton>
							))}
					</ToggleButtonGroup>
				</Card>
			</Grid>
			<Grid item xs={8}>
				{selectedItemId ? (
					<>
						{panelType === PanelType.SCRAPER_CONFIG &&
							getItem(items as ScraperConfig[]) && (
								<RenderForm item={getItem(items as ScraperConfig[])} />
							)}
						{panelType === PanelType.CLIENT_USER &&
							getItem(items as UserWithTopics[]) && (
								<RenderForm item={getItem(items as UserWithTopics[])} />
							)}
						{panelType === PanelType.MAIN_USER &&
							getItem(items as UserWithClients[]) && (
								<RenderForm item={getItem(items as UserWithClients[])} />
							)}
					</>
				) : null}
			</Grid>
		</Grid>
	);
};

export default GenericPanelToggle;
